import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/layouts/AdminLayout';
import { FileText, Plus, Download, Calendar } from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

const AdminReportsPage = () => {
  const { api } = useAuth();
  const [reports, setReports] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [newReport, setNewReport] = useState({
    project_id: '',
    month: '',
    kpis: { reach: 0, engagement: 0, leads: 0, views: 0, watch_time: 0 },
    pdf_url: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [reportsRes, projectsRes] = await Promise.all([
        api.get('/reports'),
        api.get('/projects'),
      ]);
      setReports(reportsRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const createReport = async (e) => {
    e.preventDefault();
    try {
      await api.post('/reports', newReport);
      setCreateOpen(false);
      setNewReport({
        project_id: '',
        month: '',
        kpis: { reach: 0, engagement: 0, leads: 0, views: 0, watch_time: 0 },
        pdf_url: '',
      });
      fetchData();
    } catch (error) {
      console.error('Failed to create report:', error);
    }
  };

  const getProjectName = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    return project?.name || 'Unknown';
  };

  const updateKpi = (key, value) => {
    setNewReport(prev => ({
      ...prev,
      kpis: { ...prev.kpis, [key]: parseInt(value) || 0 }
    }));
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-syne font-bold text-white">Reports</h1>
            <p className="text-fp-textMuted text-sm mt-1">{reports.length} reports uploaded</p>
          </div>
          <Button
            onClick={() => setCreateOpen(true)}
            className="btn-primary"
            data-testid="create-report-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Upload Report
          </Button>
        </div>

        {/* Reports Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reports.map((report) => (
            <div
              key={report.id}
              className="bg-fp-surface border border-white/5 rounded-sm p-6"
              data-testid={`report-card-${report.id}`}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-primary/20 rounded-sm flex items-center justify-center">
                  <FileText className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">{report.month}</h3>
                  <p className="text-fp-textMuted text-sm">{getProjectName(report.project_id)}</p>
                </div>
              </div>

              {/* KPIs */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                {Object.entries(report.kpis || {}).slice(0, 4).map(([key, value]) => (
                  <div key={key} className="p-2 bg-fp-bg rounded-sm text-center">
                    <p className="text-primary font-semibold">{value.toLocaleString()}</p>
                    <p className="text-fp-textMuted text-xs capitalize">{key.replace('_', ' ')}</p>
                  </div>
                ))}
              </div>

              <Button variant="ghost" className="w-full text-fp-textMuted hover:text-white">
                <Download className="w-4 h-4 mr-2" />
                Download PDF
              </Button>
            </div>
          ))}
        </div>

        {/* Create Report Modal */}
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent className="bg-fp-surface border-white/10 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white font-syne">Upload Report</DialogTitle>
            </DialogHeader>
            <form onSubmit={createReport} className="space-y-4">
              <div>
                <Label className="text-fp-textMuted">Project</Label>
                <Select
                  value={newReport.project_id}
                  onValueChange={(value) => setNewReport({ ...newReport, project_id: value })}
                >
                  <SelectTrigger className="mt-1 bg-fp-bg border-white/10 text-white">
                    <SelectValue placeholder="Select project" />
                  </SelectTrigger>
                  <SelectContent className="bg-fp-surface border-white/10">
                    {projects.map((project) => (
                      <SelectItem key={project.id} value={project.id}>
                        {project.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-fp-textMuted">Month</Label>
                <Input
                  value={newReport.month}
                  onChange={(e) => setNewReport({ ...newReport, month: e.target.value })}
                  placeholder="e.g., January 2026"
                  className="mt-1 bg-fp-bg border-white/10 text-white"
                  required
                />
              </div>
              <div>
                <Label className="text-fp-textMuted">KPIs</Label>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  {['reach', 'engagement', 'leads', 'views', 'watch_time'].map((kpi) => (
                    <div key={kpi}>
                      <Label className="text-fp-textMuted text-xs capitalize">{kpi.replace('_', ' ')}</Label>
                      <Input
                        type="number"
                        value={newReport.kpis[kpi]}
                        onChange={(e) => updateKpi(kpi, e.target.value)}
                        className="mt-1 bg-fp-bg border-white/10 text-white"
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-fp-textMuted">PDF URL (optional)</Label>
                <Input
                  value={newReport.pdf_url}
                  onChange={(e) => setNewReport({ ...newReport, pdf_url: e.target.value })}
                  placeholder="https://..."
                  className="mt-1 bg-fp-bg border-white/10 text-white"
                />
              </div>
              <Button type="submit" className="w-full btn-primary">
                Upload Report
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminReportsPage;
